import { Archive, ArrowLeft, Pencil, RotateCcw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DetailColumns } from '@/components/ui/detail-columns';
import type { ApiError } from '@/core/api';
import { AuditTimelinePanel } from '@/features/audit';
import { CommentsPanel } from '@/features/comments';
import { ActivitiesPanel } from '@/features/crm/activities';
import { AttachmentsPanel } from '@/features/documents';
import { CustomFieldsPanel } from '@/features/custom-fields';
import { TagsPanel } from '@/features/tags';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { CompanyProfileFields } from '../components/CompanyProfileFields';
import { useArchiveCompany, useCompany, usePurgeCompany, useRestoreCompany } from '../queries';

export function CompanyDetailPage() {
  const { publicId = '' } = useParams();
  const companyId = publicId;
  const navigate = useNavigate();

  const { data: company, isPending, isError, error } = useCompany(companyId);
  const archive = useArchiveCompany();
  const restore = useRestoreCompany();
  const purge = usePurgeCompany();

  async function handleArchive() {
    await archive.mutateAsync(companyId);
    navigate('/crm/companies');
  }

  function handleRestore() {
    restore.mutate(companyId);
  }

  function handlePurge() {
    purge.mutate(companyId, { onSuccess: () => navigate('/crm/companies') });
  }

  if (isPending) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/companies')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/companies')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{company.name}</h1>
          <Badge variant={company.is_active ? 'default' : 'secondary'}>
            {company.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/crm/companies/${company.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" aria-label="Archive company">
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this company?</AlertDialogTitle>
                <AlertDialogDescription>
                  Hide this row from the active list. You can restore it later from Archived view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive} disabled={archive.isPending}>
                  {archive.isPending ? 'Archiving…' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {company.is_archived ? (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="text-sm">
              <p className="font-medium">This company is archived.</p>
              <p className="text-muted-foreground">
                Restore it to make it visible in the active list again.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleRestore}
                disabled={restore.isPending}
                aria-label="Restore company"
              >
                <RotateCcw /> {restore.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <PurgeArchivedButton
                variant="full"
                name={company.name}
                onPurge={handlePurge}
                pending={purge.isPending}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DetailColumns
        main={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Website">
                  {company.website ? (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {company.website}
                    </a>
                  ) : (
                    '—'
                  )}
                </Field>
                <Field label="Created">{new Date(company.created).toLocaleString()}</Field>
                <Field label="Address">{company.address || '—'}</Field>
                <Field label="Country">{company.country || '—'}</Field>
                <CompanyProfileFields profile={company} />
                <Field label="Notes" className="sm:col-span-2">
                  {company.notes || '—'}
                </Field>
              </CardContent>
            </Card>

            <CustomFieldsPanel entity="crm.company" entityId={company.id} />

            <AttachmentsPanel modelLabel="crm.company" objectId={company.id} />

            <CommentsPanel modelLabel="crm.company" objectId={company.id} />
          </>
        }
        aside={
          <>
            <TagsPanel modelLabel="crm.company" objectId={company.id} />

            <ActivitiesPanel scope={{ entity: 'company', entityId: company.id }} />
            <AuditTimelinePanel entity="crm.company" entityId={company.id} />
          </>
        }
      />
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      <div className="mt-1 text-sm break-words whitespace-pre-wrap">{children}</div>
    </div>
  );
}
